import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowCollapseIcon, ArrowExpandIcon, XIcon } from '@persona-id/design-tokens/icons';
import { ComponentProps, ReactNode } from 'react';
import { css } from 'styled-components';

import PositionedPanel from 'dashboard/components-lib/MaximizablePanel/PositionedPanel';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import useToggle from 'lib/hooks/useToggle';

interface MaximizablePanelProps extends ComponentProps<typeof PositionedPanel> {
  fullscreenTop?: number;
  children: ReactNode | ((isFullscreen: boolean) => ReactNode);
  disableMaximize?: boolean;
  maximized?: boolean;
  panelPosition?: 'left' | 'right';
  containerStyleOverride?: ReturnType<typeof css>;
  onClosePanel?: () => void;
}

const MaximizablePanel = ({
  fullscreenTop,
  children,
  rightButton,
  disableMaximize,
  maximized = false,
  panelPosition = 'right',
  containerStyleOverride,
  onClosePanel,
  ...rest
}: MaximizablePanelProps) => {
  const [isFullscreen, { toggle: toggleFullscreen }] = useToggle(maximized);

  return (
    <PositionedPanel
      panelPosition={panelPosition}
      aria-expanded={isFullscreen}
      disableExpand={disableMaximize}
      getContainerStyle={
        isFullscreen
          ? ({ blockedLeft, blockedRight }) => css`
              border-top: 1px solid ${Color.CoreSlate50};
              border-left: 1px solid ${Color.CoreSlate50};

              ${containerStyleOverride};
              // Don't allow any of the following styles to be overridden since they
              // are specific to fullscreen mode
              width: calc(100vw - ${blockedLeft} - ${blockedRight});
              height: calc(100vh);
              top: ${fullscreenTop ?? FULL_SCREEN_HEADER_HEIGHT}px;
              max-height: calc(100vh - ${fullscreenTop ?? FULL_SCREEN_HEADER_HEIGHT}px);
            `
          : () => css`
              ${containerStyleOverride}
            `
      }
      rightButton={
        !disableMaximize && (
          <HStack equal spacing={Spacing[1]}>
            {rightButton}
            {!disableMaximize && (
              <Button
                aria-label='Maximize the panel'
                width='icon'
                variant='inverse'
                id='maximizable-panel__toggle-fullscreen'
                data-test='maximizable-panel__toggle-fullscreen'
                onClick={toggleFullscreen}
              >
                {isFullscreen ? <ArrowCollapseIcon /> : <ArrowExpandIcon />}
              </Button>
            )}
            {onClosePanel && (
              <Button
                aria-label='Close the panel'
                width='icon'
                variant='inverse'
                id='maximizable-panel__close-panel'
                data-test='maximizable-panel__close-panel'
                onClick={onClosePanel}
              >
                <XIcon />
              </Button>
            )}
          </HStack>
        )
      }
      {...rest}
    >
      {typeof children === 'function' ? children(isFullscreen) : children}
    </PositionedPanel>
  );
};

export default MaximizablePanel;
